import { Button, EButtonColorVariant, ETextStyleVariant, Text } from '@outdoorsyco/bonfire';
import cn from 'classnames';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import useSWR from 'swr';

import { EToastVariant, useToast } from '@/hooks/useToast';
import { getUser } from '@/redux/selectors/auth/user';
import { trackReferralCodeSharedEvent } from '@/services/analytics/referral';
import apiRequest from '@/services/apiRequest';
import { EShareMethodType, IReferralStatsProps } from '@/services/types/referral/referral';
import { getCoreApi } from '@/utility/getCoreApi';

import { ShareModal } from '../ShareModal';

export const Share = () => {
  const intl = useIntl();
  const { showToast } = useToast();

  const user = useSelector(getUser);

  const { data: referralStats } = useSWR(`${getCoreApi()}/referrals/stats`, url =>
    apiRequest<IReferralStatsProps>({ url }, true),
  );

  const [shareModalIsOpened, setShareModalIsOpened] = useState(false);

  if (!user) return null;

  const shareUrl = `${process.env.hostApp}/invite/${user.referral_code}`;

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/plain': new Blob([shareUrl], {
            type: 'text/plain',
          }),
        }),
      ]);

      trackReferralCodeSharedEvent({
        shareMethod: EShareMethodType.COPY_LINK,
        referralType: 'renter',
        currentEarningsEarned: (referralStats?.earned_credits || 0) / 100,
        currentEarningsPending: (referralStats?.pending_credits || 0) / 100,
      });

      showToast(
        EToastVariant.Success,
        intl.formatMessage({ defaultMessage: 'Link copied!', id: '/vCdX1' }),
        intl.formatMessage({ defaultMessage: 'Successfully copied the link.', id: 'exB6r0' }),
      );
    } catch {
      showToast(
        EToastVariant.Error,
        intl.formatMessage({ defaultMessage: 'Oops!', id: 'BEbOqj' }),
        intl.formatMessage({
          defaultMessage: 'Something went wrong, please try again.',
          id: '8GvVWZ',
        }),
      );
    }
  };

  return (
    <div
      className={cn(
        'w-full px-2 py-4 mx-auto bg-white md:pl-6 md:pr-4 rounded-2xl md:rounded-full max-w-[650px]',
        { invisible: !user.referral_code },
      )}>
      <div className="flex flex-col md:flex-row gap-6 md:gap-2">
        <div className="flex flex-col text-center md:overflow-hidden md:text-left gap-2 md:gap-0 grow">
          <Text variant={ETextStyleVariant.SmallRegular} className="truncate">
            {intl.formatMessage({
              defaultMessage: 'Share your referral link with friends',
              id: '9gAAHm',
            })}
          </Text>

          <Text variant={ETextStyleVariant.MediumBold}>
            <span className="break-words md:hidden">
              {/** Use zero width space for mobile so we can break the url into multiple lines in a nicer way */}
              {process.env.hostApp}&#8203;/invite&#8203;{`/${user.referral_code}`}
            </span>

            <span className="hidden md:block md:truncate">{shareUrl}</span>
          </Text>
        </div>

        <div className="flex gap-2">
          <Button
            variant={EButtonColorVariant.Secondary}
            label={intl.formatMessage({ defaultMessage: 'Share', id: 'OKhRC6' })}
            onClick={() => setShareModalIsOpened(true)}
            className="w-full"
          />

          <ShareModal
            shareUrl={shareUrl}
            isOpened={shareModalIsOpened}
            onClose={() => setShareModalIsOpened(false)}
          />

          <Button
            variant={EButtonColorVariant.Primary}
            label={intl.formatMessage({ defaultMessage: 'Copy', id: '4l6vz1' })}
            onClick={handleCopyToClipboard}
            className="w-full"
          />
        </div>
      </div>
    </div>
  );
};
